import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice} from '@reduxjs/toolkit'
import type {TreeViewOption} from '@/components/SEMetrics/tabs/TrendsViewTab/helpers/types'

// Define filter types
export type KPIFilterValue = 'commitmentMet' | 'onTimeDelivery' | 'velocity'
export type SEMetricsFilterValue = string // Release filter values are dynamic strings
export type FilterValue = string | number | boolean | KPIFilterValue | SEMetricsFilterValue

// The keys of FilterState['seMetrics'] that setSEMetricsFilter can write to. Callers should
// dispatch with these rather than a raw string; SEMetricsFilterType is derived from them so the
// accepted union and the constant can never drift apart.
export const SE_METRICS_FILTER_TYPES = {
  ESCAPE_DEFECTS_RELEASE: 'escapeDefectsReleaseFilter',
  DEFECT_LEAKAGE_RELEASE: 'defectLeakageReleaseFilter',
  AI_METRICS_MONTH: 'aiMetricsMonth',
  AI_METRICS_YEAR: 'aiMetricsYear',
  STATUS: 'statusFilter',
  PROGRAM_MODE: 'programModeFilter',
} as const

export type SEMetricsFilterType = (typeof SE_METRICS_FILTER_TYPES)[keyof typeof SE_METRICS_FILTER_TYPES]

interface FilterState {
  // Using a Record allows us to have dynamic keys for different filter components
  // Each key represents a unique filter component (e.g., 'kpiTrend', 'teamView', etc.)
  filters: Record<string, FilterValue>
  // Refresh trigger - timestamp to force components to re-fetch data
  refreshTrigger: number
  // Program-specific refresh - only refresh data for specific program ID
  refreshProgramId: string | null
  // SE Metrics specific filters - each component has its own filter
  seMetrics?: {
    escapeDefectsReleaseFilter?: string
    defectLeakageReleaseFilter?: string
    aiMetricsMonth?: string
    aiMetricsYear?: number
    programSelection?: Record<string, string[]>
    // Global filters for SE Metrics overview
    statusFilter?: string[]
    programModeFilter?: string[]
  }
  // SE Metrics Trends page - filters shared across the SE Metrics / Vulnerabilities tabs
  seMetricsTrends?: {
    selectedProgram?: TreeViewOption[]
    monthRange?: {from: string; to: string}
  }
}

const initialState: FilterState = {
  filters: {
    // Set default values for known filters
    kpiTrend: 'commitmentMet' as KPIFilterValue,
  },
  refreshTrigger: 0,
  refreshProgramId: null,
  seMetrics: {
    escapeDefectsReleaseFilter: '',
    defectLeakageReleaseFilter: '',
    aiMetricsMonth: undefined,
    aiMetricsYear: undefined,
    programSelection: {},
    statusFilter: [],
    programModeFilter: [],
  },
  seMetricsTrends: {},
}

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setFilter: (state, action: PayloadAction<{key: string; value: FilterValue}>) => {
      const {key, value} = action.payload
      state.filters[key] = value
    },
    setSEMetricsFilter: (
      state,
      action: PayloadAction<{
        filterType: SEMetricsFilterType
        value: string | number | null | string[]
      }>,
    ) => {
      const {filterType, value} = action.payload
      if (!state.seMetrics) {
        state.seMetrics = {}
      }
      state.seMetrics[filterType] = value as any
    },
    setSEMetricsProgramSelection: (state, action: PayloadAction<{dept: string; programIds: string[]}>) => {
      const {dept, programIds} = action.payload
      if (!state.seMetrics) state.seMetrics = {}
      if (!state.seMetrics.programSelection) state.seMetrics.programSelection = {}
      state.seMetrics.programSelection[dept] = programIds
    },
    setTrendsSelectedProgram: (state, action: PayloadAction<TreeViewOption[]>) => {
      if (!state.seMetricsTrends) state.seMetricsTrends = {}
      state.seMetricsTrends.selectedProgram = action.payload
    },
    setTrendsMonthRange: (state, action: PayloadAction<{from: string; to: string} | undefined>) => {
      if (!state.seMetricsTrends) state.seMetricsTrends = {}
      state.seMetricsTrends.monthRange = action.payload
    },
    resetTrendsFilters: state => {
      state.seMetricsTrends = {}
    },
    resetFilter: (state, action: PayloadAction<string>) => {
      // Remove the filter entry (will revert to default in components or undefined)
      delete state.filters[action.payload]
    },
    resetAllFilters: state => {
      // Reset to initial defaults
      state.filters = {...initialState.filters}
    },
    triggerRefresh: state => {
      // Update refresh trigger to current timestamp to force components to re-fetch data
      state.refreshTrigger = Date.now()
      state.refreshProgramId = null // Global refresh
    },
    triggerProgramRefresh: (state, action: PayloadAction<string>) => {
      // Update refresh trigger for specific program only
      state.refreshTrigger = Date.now()
      state.refreshProgramId = action.payload
    },
    clearProgramRefresh: state => {
      // Clear program-specific refresh state
      state.refreshProgramId = null
    },
  },
})

export const {
  setFilter,
  setSEMetricsFilter,
  setSEMetricsProgramSelection,
  setTrendsSelectedProgram,
  setTrendsMonthRange,
  resetTrendsFilters,
  resetFilter,
  resetAllFilters,
  triggerRefresh,
  triggerProgramRefresh,
  clearProgramRefresh,
} = filtersSlice.actions
export default filtersSlice.reducer
